import React from 'react';
import wrap from 'wraptile';
import {curry} from 'ramda';
import {compose, getFrom, isEqual, noop} from '@pluto/utils';
import {TransformComponent} from 'react-zoom-pan-pinch';
import {Slider} from '@material-ui/core';
import {
    ArtworkItem,
    ArtworkType,
    Thumbnail,
    ThumbnailRefs,
} from './ContentItemViewArtworkTypes&Const';
import ControlArrows from './ContentItemViewArtworkControlArrows';

export const getArtworkTypeTitle = (artworkType: ArtworkType) => {
    switch (artworkType) {
        case ArtworkType.Poster:
            return 'Poster';
        case ArtworkType.ThumbnailSmall:
            return 'Thumbnail Small';
        case ArtworkType.ThumbnailLarge:
            return 'Thumbnail Large';
        case ArtworkType.SeriesFeatured:
            return 'Series Featured';
        case ArtworkType.SeriesSquare:
            return 'Series Square';
        default:
            return '';
    }
};

export const handleFetchQcArtwork = wrap(
    (
        setQcArtwork: (id: number) => void,
        resetQcArtwork: () => void,
        id: number,
        isLoading: boolean
    ) => {
        if (isLoading) setQcArtwork(id);

        return resetQcArtwork;
    }
);

export const getScrollValue = (
    leftRect = 0,
    scrollLeftValue: number,
    halfThumbnailsWidth: number
) => {
    if (leftRect < halfThumbnailsWidth)
        return scrollLeftValue - (halfThumbnailsWidth - leftRect);

    return scrollLeftValue + (leftRect - halfThumbnailsWidth);
};

export const onActiveImgChange = wrap(
    (
        currentId: number,
        setCurrentImg: (value: number) => void,
        thumbnail: {
            current: Thumbnail & {
                offsetWidth: number;
                scrollLeft: number;
                scrollTo: (a: number, b: number) => void;
            };
        },
        thumbnailRefs: ThumbnailRefs,
        onImageSelect: Function = noop,
        id: number,
        event: React.MouseEvent<HTMLElement>
    ) => {
        if (event) event.stopPropagation();

        const currentImageElement = thumbnailRefs.current[id];
        const halfThumbnailsWidth = thumbnail.current?.offsetWidth / 2;
        const scrollValue = getScrollValue(
            currentImageElement?.getBoundingClientRect().left,
            thumbnail.current?.scrollLeft,
            halfThumbnailsWidth
        );
        setCurrentImg(id);
        thumbnail.current.scrollTo(scrollValue, 0);
        onImageSelect(id);
    }
);

export const getAdditionalStep = (value: number): number => {
    switch (Math.round(value)) {
        case 2:
            return 0.7;

        case 3:
            return 3.2;

        case 4:
            return 4.6;

        default:
            return 4;
    }
};

export const countTransform = (factor: number, newValue: number): number =>
    -(factor * (newValue + getAdditionalStep(newValue)));

export const handleSliderChange = curry((setTransform, event, newValue) => {
    if (newValue === 1) {
        setTransform(0, 0, newValue);
    } else {
        // factorX, factorY were chosen approximately to
        // factorX, factorY in ZoomIn, ZoomOut formula.
        // Formula isn't available on  react-zoom-pan-pinch site
        const factorX = 173.2;
        const factorY = 129.9;

        const x = countTransform(factorX, newValue);
        const y = countTransform(factorY, newValue);
        setTransform(x, y, newValue);
    }
});

export const handleModalClose = wrap((setIsModalOpen: (value: boolean) => void) => {
    setIsModalOpen(false);
});

export const handleModalOpen = wrap((setIsModalOpen: Function, name: string) => {
    setIsModalOpen(name);
});

export const getZoomedImage = curry(
    (
        zoomProps: {
            [key: string]: {
                images: ArtworkItem[];
                currentZoomId: number;
                setCurrentZoomId: Function;
                thumbnail: Thumbnail;
            };
        },
        isModalOpen: string,
        thumbnailRefs: ThumbnailRefs,
        onDebouncedImageSelect: Function,
        {zoomIn, zoomOut, scale, setTransform}
    ) => {
        const {images, currentZoomId, setCurrentZoomId, thumbnail} = zoomProps[
            isModalOpen
        ];
        const {url} = images.find(({id}) => id === currentZoomId) || {};

        return (
            <div className="qc-artwork-modal__wrapper">
                <div className="carousel__active-img_controls-modal">
                    <ControlArrows
                        images={images}
                        currentId={currentZoomId}
                        onActiveImgChangeCurried={onActiveImgChange(
                            currentZoomId,
                            setCurrentZoomId,
                            thumbnail,
                            thumbnailRefs,
                            onDebouncedImageSelect
                        )}
                    />
                    <div className="qc-artwork-modal__slider-wrapper">
                        <TransformComponent>
                            <img
                                src={url}
                                className="carousel__active-img_modal"
                                alt={url}
                            />
                        </TransformComponent>
                        <div className="qc-artwork-modal__slider">
                            <div className="tools">
                                <div
                                    role="button"
                                    onClick={zoomIn}
                                    className="tools-item tools__zoomin"
                                >
                                    +
                                </div>
                                <div
                                    role="button"
                                    onClick={zoomOut}
                                    className="tools-item tools__zoomout"
                                >
                                    -
                                </div>
                            </div>
                            <Slider
                                className="zoom-slider"
                                defaultValue={1}
                                onChange={handleSliderChange(setTransform)}
                                step={0.53}
                                value={scale}
                                min={1}
                                max={4}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
);

export const handleDebounceCallback = (selectImgAction: Function) => (id: number) =>
    selectImgAction(id);

export const handleDebounce = curry((debouncedFunction: Function, id: number) => {
    debouncedFunction(id);
});

export const setActiveImage = wrap(
    (setCurrentImg: (value: number) => void, images: ArtworkItem[]) => {
        const active = images.find(getFrom('deliverable')) as ArtworkItem;
        setCurrentImg(active?.id);
    }
);

export const filterInSmall = compose(
    isEqual(ArtworkType.ThumbnailSmall),
    getFrom('artworkType')
);

export const filterInLarge = compose(
    isEqual(ArtworkType.ThumbnailLarge),
    getFrom('artworkType')
);

export const filterInSquare = compose(
    isEqual(ArtworkType.SeriesSquare),
    getFrom('artworkType')
);

export const filterInFeatured = compose(
    isEqual(ArtworkType.SeriesFeatured),
    getFrom('artworkType')
);

export const filterInPoster = compose(
    isEqual(ArtworkType.Poster),
    getFrom('artworkType')
);
